import React, { useState } from 'react';
import { Key } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useCreateApiKey, useApiKeyAbilities } from '../../hooks/useApiKeys';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const RESOURCE_LABELS = {
    supplier: 'Suppliers',
    supplierCredential: 'Supplier Credentials',
    service: 'Services',
    invoice: 'Invoices',
    invoiceTemplate: 'Invoice Templates',
    reading: 'Readings',
    readingTemplate: 'Reading Templates',
    file: 'Files',
    scheduled_action: 'Scheduled Actions',
    reminder: 'Reminders',
    user: 'User',
};

const groupAbilities = (abilities) => {
    const groups = {};
    abilities.forEach((ability) => {
        if (ability === '*') return;
        const prefix = ability.split(':')[0];
        if (!groups[prefix]) groups[prefix] = [];
        groups[prefix].push(ability);
    });
    return groups;
};

const emptyForm = {
    name: '',
    abilities: [],
    full_access: false,
    expires_at: '',
};

const AddApiKeyModal = ({ isOpen, onClose, onCreated }) => {
    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const createApiKey = useCreateApiKey();
    const { data: allAbilities = [], isLoading: abilitiesLoading } = useApiKeyAbilities();
    const { t } = useLanguage();

    const abilityGroups = groupAbilities(allAbilities);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = t(KEYS.API_KEY_NAME_REQUIRED);
        } else if (formData.name.length > 191) {
            newErrors.name = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }
        if (!formData.full_access && formData.abilities.length === 0) {
            newErrors.abilities = t(KEYS.API_KEY_ABILITIES_REQUIRED);
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNameChange = (e) => {
        setFormData((prev) => ({ ...prev, name: e.target.value }));
        if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
    };

    const handleFullAccessToggle = (e) => {
        setFormData((prev) => ({ ...prev, full_access: e.target.checked, abilities: [] }));
        if (errors.abilities) setErrors((prev) => ({ ...prev, abilities: '' }));
    };

    const handleAbilityToggle = (ability) => {
        setFormData((prev) => {
            const next = prev.abilities.includes(ability)
                ? prev.abilities.filter((a) => a !== ability)
                : [...prev.abilities, ability];
            return { ...prev, abilities: next };
        });
        if (errors.abilities) setErrors((prev) => ({ ...prev, abilities: '' }));
    };

    const handleExpiresChange = (e) => {
        setFormData((prev) => ({ ...prev, expires_at: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                name: formData.name.trim(),
                abilities: formData.full_access ? ['*'] : formData.abilities,
            };
            if (formData.expires_at) {
                payload.expires_at = new Date(formData.expires_at).toISOString();
            }
            const result = await createApiKey.mutateAsync(payload);
            setFormData(emptyForm);
            onCreated({ token: result.token, apiKey: result.api_key });
            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.API_KEY_ADD_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData(emptyForm);
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.API_KEY_ADD_TITLE)}
            subtitle="Generate a new API key for external integrations"
            size="large"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? t(KEYS.API_KEY_ADD_SUBMITTING) : t(KEYS.API_KEY_ADD_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {submitError}
                </div>
            )}

            <div className="space-y-5">
                {/* Name */}
                <div>
                    <label className="label-base block mb-1.5">
                        Name <span className="text-status-overdue">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={handleNameChange}
                        placeholder="e.g. My Mobile App"
                        maxLength={191}
                        className={`w-full bg-surface-overlay border rounded px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent transition-colors ${
                            errors.name ? 'border-status-overdue' : 'border-surface-border focus:border-accent'
                        }`}
                    />
                    {errors.name && <p className="mt-1 text-xs text-status-overdue">{errors.name}</p>}
                </div>

                {/* Expiry */}
                <div>
                    <label className="label-base block mb-1.5">
                        {t(KEYS.API_KEY_FIELD_EXPIRY)} <span className="text-text-muted text-[10px] font-normal ml-1">{t(KEYS.API_KEY_FIELD_EXPIRY_OPTIONAL)}</span>
                    </label>
                    <input
                        type="date"
                        value={formData.expires_at}
                        onChange={handleExpiresChange}
                        min={minDate}
                        className="bg-surface-overlay border border-surface-border rounded px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent transition-colors"
                    />
                    <p className="mt-1 text-xs text-text-muted">{t(KEYS.API_KEY_FIELD_EXPIRY_HINT)}</p>
                </div>

                {/* Abilities */}
                <div>
                    <label className="label-base block mb-2">
                        {t(KEYS.API_KEY_FIELD_ABILITIES)} <span className="text-status-overdue">*</span>
                    </label>

                    {/* Full access toggle */}
                    <label className="flex items-center gap-2.5 cursor-pointer mb-3 p-2.5 rounded border border-accent/30 bg-accent/5">
                        <input
                            type="checkbox"
                            checked={formData.full_access}
                            onChange={handleFullAccessToggle}
                            className="w-4 h-4 accent-accent rounded cursor-pointer"
                        />
                        <div>
                            <span className="text-sm font-medium text-accent">{t(KEYS.API_KEY_FULL_ACCESS_LABEL)}</span>
                            <p className="text-xs text-text-muted">{t(KEYS.API_KEY_FULL_ACCESS_DESC)}</p>
                        </div>
                    </label>

                    {errors.abilities && (
                        <p className="mb-2 text-xs text-status-overdue">{errors.abilities}</p>
                    )}

                    {/* Grouped abilities */}
                    {abilitiesLoading ? (
                        <p className="text-sm text-text-muted">{t(KEYS.API_KEY_LOADING_ABILITIES)}</p>
                    ) : (
                        <div className={`space-y-3 ${formData.full_access ? 'opacity-40 pointer-events-none select-none' : ''}`}>
                            {Object.entries(abilityGroups).map(([prefix, abilities]) => (
                                <div key={prefix} className="border border-surface-border rounded overflow-hidden">
                                    <div className="px-3 py-1.5 bg-surface-overlay border-b border-surface-border">
                                        <span className="text-[10px] font-semibold text-text-muted uppercase tracking-widest">
                                            {RESOURCE_LABELS[prefix] ?? prefix}
                                        </span>
                                    </div>
                                    <div className="px-3 py-2 flex flex-wrap gap-x-4 gap-y-1.5">
                                        {abilities.map((ability) => {
                                            const action = ability.split(':')[1];
                                            return (
                                                <label
                                                    key={ability}
                                                    className="flex items-center gap-1.5 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.abilities.includes(ability)}
                                                        onChange={() => handleAbilityToggle(ability)}
                                                        className="w-3.5 h-3.5 accent-accent rounded cursor-pointer"
                                                    />
                                                    <span className="text-xs text-text-secondary capitalize">
                                                        {action}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default AddApiKeyModal;
